import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { _error, _isDev, _log } from '@shared/aux_helper_environment';
import { _ms, _timeout } from '@shared/aux_helper_functions';
import { kayshFlushValue } from '@karacas/kaysh';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AiDashboardErrorDetailsModalComponent } from 'app/ai-dashboard/components/ai-dashboard-error-details-modal/ai-dashboard-error-details-modal.component';
import { InsightDetailModalComponent } from 'app/layout/components/insights-suggestions-panel/insight-detail-modal.component';
import { ChatRequest, DataAgentSSEService, HealthCheckResponse, SSEEvent } from 'app/services/agents/data-agent-sse.service';
import { markdownToHtmlString } from 'app/test/components/data-agent-sse/markdown.pipe';
import { IanTranslateService, PrismaDynamicEnv } from 'core/services/ian-core-singleton.service';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatContentComponent, ChatMessage, ChatSessionHistoryItem, ChatType } from './models/chat-interface';

interface PromptExample {
  textKey: string;
  icon?: string;
}

@Component({
  selector: 'streaming-chat-content',
  template: `
    <div class="databricks-chat-content">
      <button
        mat-icon-button
        [matMenuTriggerFor]="promptExamplesMenu"
        class="prompt-examples-trigger"
        [matTooltip]="'AI_CHAT.PROMPT_EXAMPLES.TITLE' | pTranslate"
      >
        <mat-icon>auto_awesome</mat-icon>
      </button>

      <mat-menu #promptExamplesMenu="matMenu" class="prompt-examples-menu-panel">
        <div class="menu-header" (click)="$event.stopPropagation()">
          <mat-icon>auto_awesome</mat-icon>
          <span>{{ 'AI_CHAT.PROMPT_EXAMPLES.TITLE' | pTranslate }}</span>
        </div>
        <button mat-menu-item *ngFor="let example of promptExamples" (click)="sendExamplePrompt(example)">
          <!-- <mat-icon class="prompt-icon">auto_awesome</mat-icon> -->
          <span>{{ example.textKey | pTranslate }}</span>
        </button>
      </mat-menu>

      <div class="streaming-chat-layout">
        <!-- Sidebar de Historial (Solo Main Agent) -->
        <div
          class="history-sidebar"
          *ngIf="agentType === 'main'"
          [class.collapsed]="!isHistoryOpen"
          [class.expanded]="isHistoryOpen"
          [style.width.px]="isHistoryOpen ? sidebarWidth : 0"
        >
          <!-- Drag Handle -->
          <div class="resize-handle" (mousedown)="startDragging($event)"></div>
          <div class="history-sidebar-header">
            <!-- Título removido para evitar duplicado con el componente interno -->
            <div style="flex: 1"></div>
            <button mat-icon-button (click)="toggleHistory()" class="toggle-history-btn">
              <mat-icon>chevron_left</mat-icon>
            </button>
          </div>

          <div class="history-content-wrapper">
            <chat-session-history-menu
              [sessions]="sessionHistory"
              [isLoading]="isLoadingHistory"
              [activeSessionId]="conversationId"
              (sessionSelect)="loadSession($event)"
              (refresh)="refreshSessionHistory()"
              (newSession)="createNewSession()"
              (openSuggestion)="onOpenSuggestionDetail($event)"
            >
            </chat-session-history-menu>
          </div>
        </div>

        <!-- Área Principal del Chat -->
        <div class="chat-main-area">
          <!-- Header (simplificado) -->
          <div class="chat-header" *ngIf="agentType === 'main'">
            <div class="header-left-controls">
              <button
                mat-icon-button
                (click)="toggleHistory()"
                class="history-toggle-button"
                *ngIf="!isHistoryOpen"
                matTooltip="{{ 'AI_CHAT.HISTORY.TOOLTIP' | pTranslate }}"
              >
                <mat-icon>history</mat-icon>
              </button>
              <div class="header-title"></div>
            </div>
          </div>

          <!-- Main content area with conditional layout -->
          <div class="main-content-area" [class.has-suggestions]="hasSuggestions" [class.has-header]="agentType === 'main'">
            <div class="messages-column">
              <div class="messages" #messagesContainer>
                <div
                  *ngFor="let message of messages; let isFirst = first"
                  [ngClass]="{
                    'from-user': message.from === 'user',
                    'from-ai': message.from === 'ai',
                    streaming: message.isStreaming
                  }"
                  class="message"
                >
                  <div class="message-header">
                    <ng-container *ngIf="message.from !== 'user'">
                      <ai-icon [size]="20"></ai-icon>
                    </ng-container>
                    {{
                      message.from === 'user'
                        ? userDataName || ('AI_CHAT.MESSAGES.USER_DEFAULT' | pTranslate)
                        : ('AI_CHAT.MESSAGES.AI_NAME_AGENT' | pTranslate)
                    }}

                    <!-- Indicador de health check -->
                    <ng-container *ngIf="isCheckingHealth && message.from === 'ai'">
                      <mat-spinner diameter="16" style="margin-left: 8px;"></mat-spinner>
                      <span style="margin-left: 4px; font-size: 12px; color: #666;">{{
                        'AI_CHAT.MESSAGES.HEALTH_CHECK' | pTranslate
                      }}</span>
                    </ng-container>
                  </div>

                  <!-- Acordeón de razonamiento (mostrar durante streaming) -->
                  <mat-expansion-panel
                    *ngIf="message.isStreaming && cycleHistory.length > 0"
                    class="reasoning-accordion streaming"
                    [expanded]="false"
                  >
                    <mat-expansion-panel-header>
                      <mat-panel-title class="reasoning-title">
                        <mat-icon class="reasoning-icon">psychology</mat-icon>
                        <span class="reasoning-main-title">{{ 'AI_CHAT.REASONING.VIEW_HISTORY' | pTranslate }}</span>
                        <span class="reasoning-subtitle">{{ cycleHistory.length }} {{ 'AI_CHAT.REASONING.STEPS' | pTranslate }}</span>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="reasoning-content-wrapper">
                      <div *ngFor="let cycle of cycleHistory; let i = index; trackBy: trackByCycle" class="reasoning-cycle-item">
                        <div class="cycle-header-compact">
                          <span class="cycle-number-badge">{{ cycle.cycle }}</span>
                          <span class="cycle-title-compact">{{ cycle.title }}</span>
                        </div>
                        <div class="cycle-content-compact markdown-content" [innerHTML]="cycle.content | markdown"></div>

                        <!-- Tool calls para este ciclo -->
                        <div *ngIf="cycle.toolCalls && cycle.toolCalls.length > 0" class="tool-calls-section">
                          <div
                            *ngFor="let tool of cycle.toolCalls"
                            class="tool-call-item"
                            [class.tool-running]="tool.status === 'running'"
                            [class.tool-error]="tool.status === 'error'"
                          >
                            <div class="tool-header">
                              <mat-icon class="tool-icon">build</mat-icon>
                              <span class="tool-name">{{ tool.name }}</span>
                              <span
                                class="tool-status"
                                [class.status-running]="tool.status === 'running'"
                                [class.status-error]="tool.status === 'error'"
                              >
                                {{ tool.status === 'running' ? '⏳' : tool.status === 'error' ? '❌' : '✅' }}
                              </span>
                            </div>
                            <div class="tool-output" *ngIf="tool.output && tool.output !== translate.instant('AI_CHAT.CYCLES.EXECUTING')">
                              <pre>{{ tool.output }}</pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </mat-expansion-panel>

                  <!-- Acordeón de historial completado (antes del resultado) -->
                  <mat-expansion-panel
                    *ngIf="!message.isStreaming && message.from === 'ai' && message.cycleHistory && message.cycleHistory.length > 0"
                    class="reasoning-accordion completed"
                    [expanded]="false"
                  >
                    <mat-expansion-panel-header>
                      <mat-panel-title class="reasoning-title">
                        <mat-icon class="reasoning-icon">psychology</mat-icon>
                        <span class="reasoning-main-title">{{ 'AI_CHAT.REASONING.VIEW_HISTORY' | pTranslate }}</span>
                        <span class="reasoning-subtitle"
                          >{{ message.cycleHistory.length }} {{ 'AI_CHAT.REASONING.STEPS' | pTranslate }}</span
                        >
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="reasoning-content-wrapper">
                      <div *ngFor="let cycle of message.cycleHistory; let i = index; trackBy: trackByCycle" class="reasoning-cycle-item">
                        <div class="cycle-header-compact">
                          <span class="cycle-number-badge">{{ cycle.cycle }}</span>
                          <span class="cycle-title-compact">{{ cycle.title }}</span>
                        </div>
                        <div class="cycle-content-compact markdown-content" [innerHTML]="cycle.content | markdown"></div>

                        <!-- Tool calls para este ciclo -->
                        <div *ngIf="cycle.toolCalls && cycle.toolCalls.length > 0" class="tool-calls-section">
                          <div *ngFor="let tool of cycle.toolCalls" class="tool-call-item" [class.tool-error]="tool.status === 'error'">
                            <div class="tool-header">
                              <mat-icon class="tool-icon">build</mat-icon>
                              <span class="tool-name">{{ tool.name }}</span>
                              <span class="tool-status" [class.status-error]="tool.status === 'error'">
                                {{ tool.status === 'error' ? '❌' : '✅' }}
                              </span>
                            </div>
                            <div class="tool-output" *ngIf="tool.output">
                              <pre>{{ tool.output }}</pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </mat-expansion-panel>

                  <!-- Contenido del mensaje -->
                  <div *ngIf="!message.isStreaming">
                    <div
                      class="markdown-content"
                      [innerHTML]="message.text | markdown"
                      *ngIf="message.text.includes('**') || message.text.includes('*')"
                    ></div>
                    <div *ngIf="!message.text.includes('**') && !message.text.includes('*')">{{ message.text }}</div>
                  </div>

                  <!-- Botón para copiar contenido (solo para mensajes del agente con contenido sustancial) -->
                  <div
                    *ngIf="!message.isStreaming && message.from === 'ai' && message.text && !isWelcomeOrSystemMessage(message)"
                    class="message-header-actions"
                    style="margin-top: 12px; display: flex; gap: 8px;"
                  >
                    <button
                      mat-icon-button
                      [matMenuTriggerFor]="copyMenu"
                      [matTooltip]="'AI_CHAT.MESSAGES.COPY_TOOLTIP' | pTranslate"
                      matTooltipPosition="above"
                      class="streaming-copy-btn"
                    >
                      <mat-icon>content_copy</mat-icon>
                    </button>
                    <mat-menu #copyMenu="matMenu">
                      <button mat-menu-item (click)="copyMessageToClipboard(message, 'message')">
                        <mat-icon>chat_bubble_outline</mat-icon>
                        <span>{{ 'AI_CHAT.MESSAGES.COPY_THIS_MESSAGE' | pTranslate }}</span>
                      </button>
                      <button mat-menu-item (click)="copyMessageToClipboard(message, 'conversation')">
                        <mat-icon>forum</mat-icon>
                        <span>{{ 'AI_CHAT.MESSAGES.COPY_CONVERSATION' | pTranslate }}</span>
                      </button>
                    </mat-menu>
                    <button
                      mat-icon-button
                      [matMenuTriggerFor]="pdfMenu"
                      [matTooltip]="'AI_CHAT.MESSAGES.PDF_TOOLTIP' | pTranslate"
                      matTooltipPosition="above"
                      class="streaming-pdf-btn"
                    >
                      <mat-icon>picture_as_pdf</mat-icon>
                    </button>
                    <mat-menu #pdfMenu="matMenu">
                      <button mat-menu-item (click)="downloadAsPdf(message, 'message')">
                        <mat-icon>description</mat-icon>
                        <span>{{ 'AI_CHAT.MESSAGES.PDF_THIS_MESSAGE' | pTranslate }}</span>
                      </button>
                      <button mat-menu-item (click)="downloadAsPdf(message, 'conversation')">
                        <mat-icon>picture_as_pdf</mat-icon>
                        <span>{{ 'AI_CHAT.MESSAGES.PDF_CONVERSATION' | pTranslate }}</span>
                      </button>
                    </mat-menu>
                  </div>

                  <!-- Botón de detalles técnicos si hay error -->
                  <div *ngIf="message.error && message.errorDetails" style="margin-top: 12px;">
                    <button
                      mat-raised-button
                      color="primary"
                      (click)="openErrorDetailsModal(message.errorDetails)"
                      style="font-size: 13px;"
                    >
                      Ver detalles técnicos
                    </button>
                  </div>

                  <!-- Botón de Acción Opcional -->
                  <div *ngIf="message.actionButton && message.actionButton.payload" class="message-actions" style="margin-top: 12px;">
                    <button mat-flat-button color="primary" (click)="onMessageAction(message.actionButton)">
                      <mat-icon *ngIf="message.actionButton.icon" style="margin-right: 8px;">{{ message.actionButton.icon }}</mat-icon>
                      {{ message.actionButton.label }}
                    </button>
                  </div>

                  <!-- Contenido en streaming -->
                  <div *ngIf="message.isStreaming" class="streaming-content">
                    <!-- Indicador de ciclo actual -->
                    <div *ngIf="currentCycleTitle && isLoading" class="cycle-indicator">
                      <mat-icon class="cycle-icon">autorenew</mat-icon>
                      <span>{{ currentCycleTitle }}</span>
                    </div>

                    <!-- Contenido del ciclo actual -->
                    <div
                      class="final-output markdown-content"
                      [innerHTML]="currentCycleContent | markdown"
                      *ngIf="currentCycleContent"
                    ></div>

                    <!-- Indicador de carga cuando no hay contenido -->
                    <div *ngIf="isLoading && !currentReasoningText" class="loading-indicator">
                      <div class="loadingWrapp">
                        <mat-spinner color="accent" diameter="15"></mat-spinner>
                      </div>
                      {{ 'AI_CHAT.MESSAGES.PROCESSING' | pTranslate }}
                    </div>
                  </div>

                  <!-- Historial de ciclos (opcional) -->
                  <div *ngIf="message.isStreaming && cycleHistory.length > 1 && showCycleHistory" class="cycle-history">
                    <h4>{{ 'AI_CHAT.REASONING.PROCESSING_HISTORY' | pTranslate }}:</h4>
                    <div *ngFor="let cycle of cycleHistory; trackBy: trackByCycle" class="cycle-item">
                      <strong>{{ cycle.title }}</strong>
                      <div class="markdown-content" [innerHTML]="cycle.content | markdown"></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading overlay for session load -->
              <div *ngIf="isLoading && messages.length === 0" class="chat-loading-overlay">
                <mat-spinner diameter="40"></mat-spinner>
                <span class="loading-text" *ngIf="false">{{ 'AI_CHAT.HISTORY.LOADING' | pTranslate }}</span>
              </div>
            </div>

            <!-- Suggestions Column -->
            <div class="suggestions-column" *ngIf="showInsightsSuggestions && false" [style.display]="hasSuggestions ? 'block' : 'none'">
              <!-- Insights Suggestions Container -->
              <insights-suggestions-container #suggestionsContainer [autoLoad]="false" [showOnlyPending]="true">
              </insights-suggestions-container>
            </div>
          </div>

          <!-- Mensaje de estado de API si no está disponible -->
          <div *ngIf="apiHealthStatus === 'unhealthy' && !isCheckingHealth" class="api-status-warning">
            <mat-icon color="warn">warning</mat-icon>
            <span>{{ 'AI_CHAT.ERRORS.SERVICE_UNAVAILABLE' | pTranslate }}</span>
            <button mat-button color="primary" (click)="checkApiHealth()" [disabled]="isCheckingHealth">
              <mat-icon>refresh</mat-icon>
              {{ 'AI_CHAT.ACTIONS.RETRY' | pTranslate }}
            </button>
          </div>

          <div *ngIf="apiHealthStatus === 'starting'" class="api-status-warning">
            <mat-icon color="warn">warning</mat-icon>
            <span>{{ healthMessage }}</span>
          </div>

          <!-- Prompt Examples Fixed Bar (arriba del input) -->
          <div *ngIf="showPromptExamples" class="prompt-examples-fixed-bar">
            <div class="prompt-examples-grid">
              <button
                *ngFor="let example of promptExamples"
                class="prompt-example-card"
                (click)="sendExamplePrompt(example)"
                [disabled]="isLoading"
              >
                <mat-icon class="prompt-icon">auto_awesome</mat-icon>
                <span class="prompt-example-text">{{ example.textKey | pTranslate }}</span>
              </button>
            </div>
          </div>

          <chat-input
            [(value)]="userInput"
            [placeholder]="'AI_CHAT.INPUT.PLACEHOLDER' | pTranslate"
            [disabled]="apiHealthStatus === 'unhealthy'"
            [isProcessing]="isLoading"
            [rows]="2"
            [resetTooltip]="'AI_CHAT.INPUT.RESET_TOOLTIP' | pTranslate"
            [hasConversation]="messages.length > 1"
            [showChatTypeSelector]="showChatTypeSelector"
            [selectedChatType]="selectedChatType"
            [availableChatTypes]="availableChatTypes"
            (send)="onSendMessage($event)"
            (stop)="stopStream()"
            (reset)="resetConversation()"
            (chatTypeChange)="onChatTypeChange($event)"
          >
          </chat-input>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./streaming-chat-content-clean.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StreamingChatContentComponent implements OnInit, AfterViewInit, OnDestroy, ChatContentComponent {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('suggestionsContainer') private suggestionsContainer: any;

  @Output() onResetRequest = new EventEmitter<void>();
  @Output() chatTypeChange = new EventEmitter<ChatType>(); // Nuevo output
  @Input() isInPanelContext = false;
  @Input() showChatTypeSelector = false;
  @Input() selectedChatType: ChatType = ChatType.STREAMING;
  @Input() availableChatTypes: { value: ChatType; label: string; description: string }[] = [];
  @Input() agentType = 'main'; // Tipo de agente: 'main', 'insight', 'pricing', etc.

  messages: ChatMessage[] = [];
  userInput = '';
  userDataName = null;

  // Propiedades requeridas por ChatContentComponent interface
  loading = false;

  // Estados de streaming
  isLoading = false;
  hasError = false;
  errorMessage = '';
  isStreamComplete = false;

  // Sistema de streaming basado en test-data-agent-sse
  currentReasoningText = ''; // Contenido del reasoning del ciclo actual
  currentCycleContent = ''; // Contenido visible del ciclo actual (se limpia en cada ciclo)
  finalOutput = ''; // Output final cuando termina todo
  cycleHistory: Array<{
    cycle: number;
    title: string;
    content: string;
    toolCalls?: Array<{ name: string; input: string; output: string; status?: string }>;
  }> = [];
  currentCycle = 0;
  currentCycleTitle = '';
  pendingCycleReset = false;
  hasValidOutput = false;
  showCycleHistory = false;

  private streamErrorHandled = false;

  // Configuración del agente
  conversationId = '';

  // Health check
  isCheckingHealth = false;
  apiHealthStatus: 'unknown' | 'healthy' | 'starting' | 'unhealthy' = 'unknown';
  healthMessage = '';
  private healthCheckAttempt = 0;
  private readonly healthCheckBackoffMs = [2000, 5000, 10000, 20000];
  private healthCheckRetryTimer?: number;

  // Suggestions state
  hasSuggestions = false;
  showInsightsSuggestions = false;

  // Session history state
  sessionHistory: ChatSessionHistoryItem[] = [];
  isLoadingHistory = false;
  isHistoryOpen = true; // Default open
  private useHistoryService = false; // Flag para habilitar el servicio cuando la API esté lista

  // Sidebar Resize Props
  sidebarWidth = 280;
  minSidebarWidth = 200;
  maxSidebarWidth = 600;
  isDragging = false;
  startX = 0;
  startWidth = 0;

  // Ejemplos de prompts
  promptExamples: PromptExample[] = [
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.INSIGHT_PRICE_OPTIMIZATION' },
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.INSIGHT_PERFORMANCE_ANOMALIES' },
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.INSIGHT_STOCK_RECOMMENDATIONS' },
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.INSIGHT_STRATEGIC_OPPORTUNITIES' },
  ];
  showPromptExamples = true; // Mostrar ejemplos por defecto

  private lastInsightsCheckTime: number = Date.now();
  private sseSubscription?: Subscription;
  private sessionLoadSubscription?: Subscription; // Controla la carga de sesión activa (evita race conditions)
  private _unsubscribeAll: Subject<any>;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private prismaDynamicEnv: PrismaDynamicEnv,
    private sseService: DataAgentSSEService,
    private cdr: ChangeDetectorRef,
    private translate: IanTranslateService,
    private snackBar: MatSnackBar,
    private _fuseSidebarService: FuseSidebarService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    _log('Streaming Chat Content - ngOnInit');
    // Verificar si se deben mostrar las sugerencias según brandConfig
    this.showInsightsSuggestions = !!this.prismaDynamicEnv.getConf(
      'brandCustomization.featuresEnabled.blocksEnabled.PRISMA-ASSISTANT-AI-DATA-CHAT-DEMO'
    );

    _log('[StreamingChatContent] showInsightsSuggestions:', this.showInsightsSuggestions);

    // Suscribirse a solicitudes de carga de sesión externas
    this.sseService.loadSessionRequest$.pipe(takeUntil(this._unsubscribeAll)).subscribe(sessionId => {
      if (!this.shouldHandleSessionRequest()) {
        return;
      }
      this._handleExternalLoadSession(sessionId);
    });

    // Inicializar historial de sesiones con datos hardcoded
    this.initializeSessionHistory();

    this.initializeChat();
  }

  ngAfterViewInit(): void {
    _log('[StreamingChatContent] ngAfterViewInit - hasSuggestions initial:', this.hasSuggestions);
    _log('[StreamingChatContent] ngAfterViewInit - suggestionsContainer available:', !!this.suggestionsContainer);

    // Verificar sugerencias después de que la vista se inicialice
    setTimeout(() => {
      _log('[StreamingChatContent] Checking suggestions after view init');
      _log('[StreamingChatContent] Container available in timeout:', !!this.suggestionsContainer);
      this.checkForSuggestions();
    }, 100);

    // Verificación adicional más tarde
    setTimeout(() => {
      _log('[StreamingChatContent] Second check for suggestions');
      this.checkForSuggestions();
    }, 2000);
  }

  ngOnDestroy(): void {
    if (this.healthCheckRetryTimer) {
      clearTimeout(this.healthCheckRetryTimer);
    }
    this.stopStream();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  /**
   * Inicializa el historial de sesiones con datos hardcoded.
   * TODO: Reemplazar con llamada a API cuando esté disponible
   */
  private initializeSessionHistory(): void {
    this.loadHistoryAndSuggestions();
  }

  private loadHistoryAndSuggestions(): void {
    this.isLoadingHistory = true;

    forkJoin({
      sessions: this.sseService.getSessionHistory(this.agentType),
      suggestions: this.sseService.getInsightsSuggestions(false),
    })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: ({ sessions, suggestions }) => {
          this.sessionHistory = sessions.map(session => {
            // Try to match by insight_id matching session.id
            const linkedSuggestion = suggestions.find(s => {
              const suggestionId = s.insight_id || s.id;
              return String(suggestionId) === String(session.id);
            });

            return {
              ...session,
              suggestion: linkedSuggestion,
            };
          });
          this.isLoadingHistory = false;
          this.cdr.detectChanges();
        },
        error: err => {
          _error('[SessionHistory] Error loading:', err);
          this.isLoadingHistory = false;
        },
      });
  }

  onOpenSuggestionDetail(suggestion: any): void {
    if (!suggestion) return;

    this.dialog.open(InsightDetailModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'insight-detail-dialog',
      data: {
        id: suggestion.id,
        title: suggestion.title,
        description: suggestion.description,
        date: suggestion.date,
        type: suggestion.type,
        priority: suggestion.priority,
        estimatedImpact: suggestion.estimatedImpact,
        detailedReport: suggestion.detailedReport || 'No hay informe detallado disponible.',
        tableData: suggestion.tableData,
        insight_id: suggestion.insight_id,
      },
    });
  }

  /**
   * Refresca el historial de sesiones.
   * TODO: Implementar llamada a API cuando esté disponible
   */
  refreshSessionHistory(): void {
    _log('[SessionHistory] Refreshing session history');
    this.loadHistoryAndSuggestions();
  }

  /**
   * Maneja la solicitud externa de carga de sesión (ej. desde insights)
   */
  private _handleExternalLoadSession(sessionId: string): void {
    _log('[StreamingChatContent] Handling external load session request:', sessionId);

    // Pequeño delay para asegurar que la inicialización ha terminado y la UI está lista
    setTimeout(() => {
      // Verificar si ya estamos en esa sesión
      if (this.conversationId === sessionId) {
        _log('[StreamingChatContent] Session already active:', sessionId);
        return;
      }

      // Buscar en el historial cargado
      const existingSession = this.sessionHistory.find(s => s.id === sessionId || s.conversationId === sessionId);

      if (existingSession) {
        _log('[StreamingChatContent] Found session in history, loading:', existingSession.name);
        this.loadSession(existingSession);
      } else {
        // Si no está en el historial, intentamos buscar si hay una sugerencia asociada antes de crear la temporal
        _log('[StreamingChatContent] Session not found in history, attempting direct load:', sessionId);

        this.sseService
          .getInsightsSuggestions(false)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(suggestions => {
            const linkedSuggestion = suggestions.find(s => {
              const suggestionId = s.insight_id || s.id;
              return String(suggestionId) === String(sessionId);
            });

            // Creamos un objeto de sesión temporal con la sugerencia si existe
            const tempSession: ChatSessionHistoryItem = {
              id: sessionId,
              name: 'Cargando sesión...',
              description: '',
              date: new Date(),
              messageCount: 0,
              agentType: this.agentType,
              lastMessage: '',
              suggestion: linkedSuggestion,
            };

            this.loadSession(tempSession);

            // Opcional: refrescar historial para ver si aparece
            this.refreshSessionHistory();
          });
      }
    }, 100);
  }

  /**
   * Carga una sesión del historial.
   * TODO: Implementar carga completa de mensajes desde API
   */
  loadSession(session: ChatSessionHistoryItem): void {
    _log('[SessionHistory] Loading session:', session.id, session.name);

    if (!this.isInPanelContext) {
      this.router.navigate(['/ai-chat/agent/session', session.id]);
    }

    // Cancelar cualquier carga de sesión en vuelo para evitar race conditions
    if (this.sessionLoadSubscription) {
      this.sessionLoadSubscription.unsubscribe();
      this.sessionLoadSubscription = undefined;
    }

    // Limpiar estado actual antes de cargar
    this.clearStreamingState();
    this.messages = []; // Limpiar mensajes para mostrar loading
    this.isLoading = true; // Activar loading
    this.loading = true;

    this.sessionLoadSubscription = this.sseService
      .getSessionMessages(session.id)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: ({ messages, conversationId: resolvedId }) => {
          this.messages = messages;

          // Prioridad para el conversationId:
          // 1. El ID real extraído de la respuesta de la API (item.conversation_id) — más confiable
          // 2. El conversationId que ya teníamos en el objeto session (del historial list)
          // 3. session.id (ID numérico) — último recurso, puede causar pérdida de contexto
          const finalConversationId = resolvedId || session.conversationId || session.id;
          this.conversationId = finalConversationId;

          this.isLoading = false;
          this.loading = false;

          // Si la sesión tiene una sugerencia asociada, agregar mensaje con botón
          if (session.suggestion) {
            this.messages.push({
              from: 'ai',
              text: 'El agente ha generado sugerencias de cambios de precios.',
              actionButton: {
                label: 'Ver Sugerencia',
                action: 'view_suggestion_detail',
                payload: session.suggestion,
                icon: 'lightbulb',
              },
            });
          }

          this.scrollToBottomWhitDelay();
          this.cdr.detectChanges();
          _log('[SessionHistory] Session messages loaded:', messages.length);
          _log(
            '[SessionHistory] conversationId resolved — from API:',
            resolvedId,
            '| session.conversationId:',
            session.conversationId,
            '| final:',
            finalConversationId
          );
        },
        error: err => {
          _error('[SessionHistory] Error loading messages:', err);
          this.isLoading = false;

          // Mensaje de error amigable en el chat
          this.messages = [
            {
              from: 'ai',
              text: `❌ **Error al cargar la sesión**\n\nNo se pudieron recuperar los mensajes de la sesión "${
                session.name
              }".\n\n*Detalle del error:* ${err.message || 'Error desconocido'}`,
            },
          ];
        },
      });
  }

  /**
   * Crea una nueva sesión de chat.
   */
  createNewSession(): void {
    _log('[SessionHistory] Creating new session');
    if (!this.isInPanelContext) {
      this.router.navigate(['/ai-chat/agent']);
    }
    this.resetConversation();
    // Generar un nuevo conversationId para que el backend trate esto como una sesión nueva
    this.conversationId = `streaming-conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    _log('[SessionHistory] New conversationId generated:', this.conversationId);
  }

  private initializeChat(): void {
    this.resetConversation();

    // Generar conversationId único
    this.conversationId = `streaming-conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Verificar health de la API
    this.checkApiHealth();

    // Obtener datos del usuario
    this.oidcSecurityService
      .getUserData()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(userData => {
        if (userData?.name) this.userDataName = userData?.name;
      });
  }

  private shouldHandleSessionRequest(): boolean {
    if (this.isInPanelContext) {
      return true;
    }

    return this.router.url.includes('/ai-chat/agent/session/');
  }

  resetConversation() {
    this.messages = [{ from: 'ai', text: this.translate.instant('AI_CHAT.MESSAGES.STREAMING_WELCOME') }];
    this.userInput = '';
    this.hasError = false;
    this.errorMessage = '';
    this.showPromptExamples = true; // Mostrar ejemplos de nuevo al resetear
    this.clearStreamingState();
    this.clearSuggestions();
    this.onResetRequest.emit();
  }

  /**
   * Verifica el estado de salud de la API del Data Agent
   */
  async checkApiHealth(): Promise<void> {
    this.isCheckingHealth = true;
    this.apiHealthStatus = 'unknown';

    try {
      _log('Checking Data Agent API health...');

      const healthResult: HealthCheckResponse = await this.sseService.checkHealth();

      this.apiHealthStatus = healthResult.status;
      this.healthMessage = healthResult.message || '';

      if (healthResult.status === 'healthy') {
        this.healthCheckAttempt = 0;
        if (this.healthCheckRetryTimer) {
          clearTimeout(this.healthCheckRetryTimer);
        }
      }

      if (healthResult.status === 'unhealthy') {
        if (this.isWarmupTimeout(healthResult.message)) {
          this.enterStartingState('El servicio se esta iniciando. Reintentando...');
          return;
        }
        // Mostrar mensaje de error en el chat
        const errorMessage: ChatMessage = {
          from: 'ai',
          text: `**API No Disponible**\n\n${healthResult.message}\n\n*Por favor, contacta al administrador del sistema o intenta más tarde.*`,
        };

        // Reemplazar el mensaje de bienvenida con el error
        if (this.messages.length > 0) {
          this.messages[0] = errorMessage;
        } else {
          this.messages.push(errorMessage);
        }

        _error('Data Agent API health check failed:', healthResult);
      } else {
        _log('Data Agent API is healthy:', healthResult);
      }
    } catch (error) {
      _error('Error during health check:', error);

      if (this.isWarmupTimeout(error)) {
        this.enterStartingState('El servicio se esta iniciando. Reintentando...');
        return;
      }

      this.apiHealthStatus = 'unhealthy';
      this.healthMessage = this.translate.instant('AI_CHAT.ERRORS.API_HEALTH_CHECK_FAILED');

      // Mostrar mensaje de error genérico
      const title = this.translate.instant('AI_CHAT.ERRORS.CONNECTION_ERROR_TITLE');
      const msg = this.translate.instant('AI_CHAT.ERRORS.CONNECTION_ERROR_MESSAGE');
      const errorMessage: ChatMessage = {
        from: 'ai',
        text: `❌ **${title}**\n\n${msg}`,
      };

      if (this.messages.length > 0) {
        this.messages[0] = errorMessage;
      } else {
        this.messages.push(errorMessage);
      }
    } finally {
      this.isCheckingHealth = false;
      this.cdr.detectChanges();
    }
  }

  private isWarmupTimeout(error: unknown): boolean {
    const message = typeof error === 'string' ? error : (error as Error)?.message || '';
    return message.toLowerCase().includes('timeout');
  }

  private enterStartingState(message: string): void {
    this.apiHealthStatus = 'starting';
    this.healthMessage = message;
    this.scheduleHealthCheckRetry();
  }

  private scheduleHealthCheckRetry(): void {
    if (this.healthCheckAttempt >= this.healthCheckBackoffMs.length) {
      this.apiHealthStatus = 'unhealthy';
      return;
    }

    const delay = this.healthCheckBackoffMs[this.healthCheckAttempt];
    this.healthCheckAttempt += 1;

    if (this.healthCheckRetryTimer) {
      clearTimeout(this.healthCheckRetryTimer);
    }

    this.healthCheckRetryTimer = window.setTimeout(() => {
      this.checkApiHealth();
    }, delay);
  }

  private clearStreamingState(): void {
    this.stopStream();
    this.currentReasoningText = '';
    this.currentCycleContent = '';
    this.finalOutput = '';
    this.cycleHistory = [];
    this.currentCycle = 0;
    this.currentCycleTitle = '';
    this.pendingCycleReset = false;
    this.isStreamComplete = false;
    this.showCycleHistory = false;
    this.hasValidOutput = false;
    this.isLoading = false;
    this.loading = false;
    this.streamErrorHandled = false;
  }

  async onSendMessage(message: string) {
    if (!message.trim()) return;

    // Ocultar los ejemplos de prompt cuando el usuario envía un mensaje
    this.showPromptExamples = false;

    // Verificar si la API está disponible
    if (this.apiHealthStatus === 'unhealthy') {
      const errorMessage: ChatMessage = {
        from: 'ai',
        text: `**No se puede procesar la consulta**\n\n${this.healthMessage}\n\n*Verifica el estado de la API antes de continuar.*`,
      };
      this.messages.push(errorMessage);
      return;
    }

    // Limpiar estado ANTES de agregar mensajes
    this.clearStreamingState();

    // Forzar detección de cambios después de limpiar
    this.cdr.detectChanges();

    // Agregar mensaje del usuario
    this.messages.push({ from: 'user', text: message });

    // Crear mensaje de streaming placeholder
    const streamingMessage: ChatMessage = {
      from: 'ai',
      text: '',
      isStreaming: true,
    };
    this.messages.push(streamingMessage);

    this.userInput = '';
    this.isLoading = true;
    this.loading = true;

    await this.scrollToBottomWhitDelay(50);

    // Iniciar streaming
    this.startStreaming(message);
  }

  async sendExamplePrompt(example: PromptExample) {
    const promptText = this.translate.instant(example.textKey);
    if (promptText && promptText !== example.textKey) {
      await this.onSendMessage(promptText);
    }
  }

  private async startStreaming(message: string): Promise<void> {
    const request: ChatRequest = {
      message: message,
      conversation_id: this.conversationId,
      agent_type: this.agentType,
    };

    try {
      _log('Starting SSE stream with request:', request);

      this.sseSubscription = this.sseService.startDataAgentStreamWithFetch(request).subscribe({
        next: (event: SSEEvent) => {
          this.processStreamEvent(event);
          _log('SSE Event received:', event);
        },
        error: (error: SSEEvent | Error) => {
          _error('SSE Stream Error:', error);
          // Delegar el manejo de error al método handleStreamError
          this.handleStreamError(error);
          this.stopStream();
        },
        complete: () => {
          _log('SSE Stream Completed');
          this.isStreamComplete = true;
          this.isLoading = false;
          this.loading = false;
          this.stopStream();
        },
      });
    } catch (error) {
      _error('Error starting stream:', error);
      this.hasError = true;
      this.isLoading = false;
      this.loading = false;
    }
  }

  private processStreamEvent(event: SSEEvent): void {
    const eventType = event.type?.toLowerCase();

    switch (eventType) {
      case 'init':
        if (event.message) {
          _log('[INIT]', event.message);
        }
        break;

      case 'cycle_start':
        const newCycle = event.cycle || this.currentCycle + 1;
        const newCycleTitle = event.message || event.content || `Procesando ciclo ${newCycle}`;

        // Guardar el ciclo anterior en el historial si tiene contenido
        if (this.hasSignificantContent(this.currentReasoningText)) {
          this.savePreviousCycle();
        }

        // Limpiar el contenido visible del ciclo anterior
        this.currentCycleContent = '';
        this.currentReasoningText = '';

        this.currentCycle = newCycle;
        this.currentCycleTitle = newCycleTitle;
        _log(`Started cycle ${this.currentCycle}: ${this.currentCycleTitle}`);
        break;

      case 'text_chunk':
        const textChunk = this.extractTextFromEvent(event);
        if (textChunk) {
          // Acumular en el contenido visible del ciclo actual
          this.currentCycleContent += textChunk;
          this.hasValidOutput = true;
        }
        break;
      case 'reasoning':
        const reasoningContent = this.extractTextFromEvent(event);
        if (reasoningContent) {
          // Actualizar currentReasoningText para el ciclo actual
          this.currentReasoningText = reasoningContent;
          // También mostrar en el contenido visible del ciclo actual
          this.currentCycleContent = reasoningContent;

          // Guardar o actualizar en el historial de ciclos
          const cycle = event.cycle || this.currentCycle;
          const existingIndex = this.cycleHistory.findIndex(c => c.cycle === cycle);

          if (existingIndex >= 0) {
            this.cycleHistory[existingIndex].content = reasoningContent;
          } else {
            this.cycleHistory.push({
              cycle: cycle,
              title: this.currentCycleTitle || `${this.translate.instant('AI_CHAT.CYCLES.PROCESSING_STEP')} ${cycle}`,
              content: reasoningContent,
              toolCalls: [],
            });
          }
        }
        break;

      case 'tool_start':
        const toolName = event.name || this.translate.instant('AI_CHAT.CYCLES.UNKNOWN_TOOL');
        const toolInput = event.input || '';
        const toolId = event.tool_id || '';
        const toolCycle = event.cycle || this.currentCycle;

        _log(`[TOOL_START] ${toolName} in cycle ${toolCycle}`);

        // Encontrar o crear el ciclo en el historial
        let cycleIndex = this.cycleHistory.findIndex(c => c.cycle === toolCycle);
        if (cycleIndex < 0) {
          this.cycleHistory.push({
            cycle: toolCycle,
            title: this.currentCycleTitle || `${this.translate.instant('AI_CHAT.CYCLES.PROCESSING_STEP')} ${toolCycle}`,
            content: this.currentReasoningText || '',
            toolCalls: [],
          });
          cycleIndex = this.cycleHistory.length - 1;
        }

        // Inicializar toolCalls si no existe
        if (!this.cycleHistory[cycleIndex].toolCalls) {
          this.cycleHistory[cycleIndex].toolCalls = [];
        }

        // Agregar el tool call con información pendiente
        this.cycleHistory[cycleIndex].toolCalls!.push({
          name: toolName,
          input: toolInput,
          output: this.translate.instant('AI_CHAT.CYCLES.EXECUTING'),
          status: 'running',
        });
        break;

      case 'tool_result':
        const resultToolId = event.tool_id || '';
        const resultToolName = event.name || '';
        const resultOutput = event.output || '';
        const resultStatus = event.status || 'success';
        const resultCycle = event.cycle || this.currentCycle;

        _log(`[TOOL_RESULT] ${resultToolName} in cycle ${resultCycle}`);

        // Encontrar el ciclo correspondiente
        const resultCycleIndex = this.cycleHistory.findIndex(c => c.cycle === resultCycle);
        if (resultCycleIndex >= 0 && this.cycleHistory[resultCycleIndex].toolCalls) {
          // Buscar el tool call correspondiente por nombre (último que coincida)
          const toolCalls = this.cycleHistory[resultCycleIndex].toolCalls!;
          for (let i = toolCalls.length - 1; i >= 0; i--) {
            if (toolCalls[i].name === resultToolName && toolCalls[i].status === 'running') {
              toolCalls[i].output = resultOutput;
              toolCalls[i].status = resultStatus;
              break;
            }
          }
        }
        break;

      case 'complete':
        this.handleStreamComplete();
        break;

      case 'error':
        this.handleStreamError(event);
        break;
    }

    this.cdr.detectChanges();
  }

  private extractTextFromEvent(event: SSEEvent): string {
    return (
      event.content ||
      event.text ||
      event.message ||
      (event.data && typeof event.data === 'string' ? event.data : '') ||
      (event.data && event.data.text ? event.data.text : '') ||
      (event.data && event.data.content ? event.data.content : '')
    );
  }

  private addTextToCurrentCycle(text: string): void {
    if (!text) return;

    if (this.pendingCycleReset) {
      this.currentReasoningText = text;
      this.pendingCycleReset = false;
    } else {
      if (text.length > this.currentReasoningText.length + 100) {
        this.currentReasoningText = text;
      } else if (!this.currentReasoningText.includes(text.trim())) {
        this.currentReasoningText += text;
      }
    }
  }

  private replaceCurrentCycleContent(text: string): void {
    if (!text) return;
    this.currentReasoningText = text;
    if (this.pendingCycleReset) {
      this.pendingCycleReset = false;
    }
  }

  private hasSignificantContent(text: string): boolean {
    return text.trim().length > 10;
  }

  private savePreviousCycle(): void {
    if (this.currentCycle > 0 && this.hasSignificantContent(this.currentReasoningText)) {
      const existingIndex = this.cycleHistory.findIndex(c => c.cycle === this.currentCycle);

      if (existingIndex >= 0) {
        this.cycleHistory[existingIndex].content = this.currentReasoningText;
        this.cycleHistory[existingIndex].title = this.currentCycleTitle;
      } else {
        this.cycleHistory.push({
          cycle: this.currentCycle,
          title: this.currentCycleTitle,
          content: this.currentReasoningText,
        });
      }
    }
  }

  private checkForNewInsights(): void {
    if (!this.showInsightsSuggestions) return;

    _log('[StreamingChatContent] Checking for new insights since:', new Date(this.lastInsightsCheckTime));

    this.sseService
      .getInsightsSuggestions()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(suggestions => {
        // Filtrar nuevas: creadas después de lastInsightsCheckTime
        const freshInsights = suggestions.filter(s => {
          const sDate = s.date ? new Date(s.date).getTime() : 0;
          const generatedDate = s.generated_at ? new Date(s.generated_at).getTime() : 0;
          return sDate > this.lastInsightsCheckTime || generatedDate > this.lastInsightsCheckTime;
        });

        if (freshInsights.length > 0) {
          _log('[StreamingChatContent] Found new insights:', freshInsights.length);

          // Mostrar SnackBar
          const key =
            freshInsights.length === 1 ? 'AI_CHAT.NOTIFICATIONS.NEW_SUGGESTIONS_ONE' : 'AI_CHAT.NOTIFICATIONS.NEW_SUGGESTIONS_MANY';
          const message = this.translate.instant(key).replace('{{count}}', freshInsights.length.toString());
          const action = this.translate.instant('AI_CHAT.NOTIFICATIONS.VIEW_ACTION');

          const snackRef = this.snackBar.open(message, action, {
            duration: _ms('10s'),
          });

          snackRef.onAction().subscribe(() => {
            // Abrir modal de detalle de la primera sugerencia
            if (freshInsights.length > 0) {
              this.onOpenSuggestionDetail(freshInsights[0]);
            }
          });

          // Agregar mensaje accionable al chat
          const suggestionText = freshInsights.length === 1 ? 'sugerencia relevante' : 'sugerencias relevantes';
          const buttonLabel = freshInsights.length === 1 ? 'Ver Sugerencia' : 'Ver Sugerencias';

          this.messages.push({
            from: 'ai',
            text: `He encontrado ${freshInsights.length} ${suggestionText} basadas en este análisis.`,
            actionButton: {
              label: buttonLabel,
              action: 'view_suggestion_detail',
              payload: freshInsights[0], // Pasar la primera sugerencia como payload
              icon: 'lightbulb',
            },
          });
          this.scrollToBottomWhitDelay();
        }

        // Actualizar timestamp
        this.lastInsightsCheckTime = Date.now();

        // Actualizar la vista de sugerencias llamando al método existente
        this.loadInsightSuggestions();
      });
  }

  private handleStreamComplete(): void {
    this.isLoading = false;
    this.loading = false;
    this.isStreamComplete = true;

    // Guardar el último ciclo en el historial
    if (this.hasSignificantContent(this.currentReasoningText) || this.hasSignificantContent(this.currentCycleContent)) {
      this.savePreviousCycle();
    }

    // El finalOutput debe ser el contenido del último ciclo
    if (this.currentCycleContent.trim()) {
      this.finalOutput = this.currentCycleContent;
      this.hasValidOutput = true;
    } else if (this.currentReasoningText.trim()) {
      this.finalOutput = this.currentReasoningText;
      this.hasValidOutput = true;
    } else if (this.cycleHistory.length > 0) {
      // Si no hay contenido actual, usar el último del historial
      this.finalOutput = this.cycleHistory[this.cycleHistory.length - 1].content;
      this.hasValidOutput = true;
    }

    // Actualizar el mensaje de streaming con el contenido final
    this.updateStreamingMessage();

    // Cargar sugerencias y verificar nuevas después de completar el streaming
    if (this.showInsightsSuggestions) {
      // Bust the kaysh cache so getInsightsSuggestions fetches fresh data from the API
      try {
        kayshFlushValue('getInsightsSuggestions', null);
      } catch (_) {}
      this.checkForNewInsights();
    }

    _log('Stream completed successfully');
  }

  private updateStreamingMessage(): void {
    const streamingMessageIndex = this.messages.findIndex(m => m.isStreaming);
    if (streamingMessageIndex >= 0) {
      this.messages[streamingMessageIndex].isStreaming = false;
      this.messages[streamingMessageIndex].text = this.finalOutput || this.currentReasoningText;
      // Guardar el historial de ciclos en el mensaje para mostrarlo después
      this.messages[streamingMessageIndex].cycleHistory = [...this.cycleHistory];
    }
  }

  private handleStreamError(error: SSEEvent | Error): void {
    if (this.streamErrorHandled) {
      return;
    }

    this.streamErrorHandled = true;
    this.isLoading = false;
    this.loading = false;
    this.hasError = true;

    // Extraer mensaje de error más detallado
    let errorDetail = '';
    let httpStatus: number | undefined;
    let httpStatusText: string | undefined;
    let requestUrl: string | undefined;

    if (error instanceof Error) {
      errorDetail = error.message;
    } else {
      const sseError = error as SSEEvent;
      errorDetail = sseError.error || sseError.message || 'Error desconocido';
      // Intentar extraer detalles HTTP si están disponibles
      const errorObj = sseError as any;
      httpStatus = errorObj.status;
      httpStatusText = errorObj.statusText;
      requestUrl = errorObj.url;
    }

    // Crear objeto de errorDetails para el modal
    const errorDetails = {
      message: errorDetail,
      timestamp: new Date().toISOString(),
      httpStatus: httpStatus,
      httpStatusText: httpStatusText,
      requestUrl: requestUrl,
      originalError: error,
    };

    // Determinar mensaje amigable para el usuario
    let userFriendlyMessage = 'Ocurrió un error al procesar tu mensaje.';
    let suggestions = '';

    // Identificar tipo de error y dar mensaje específico
    if (
      errorDetail.toLowerCase().includes('failed to fetch') ||
      errorDetail.toLowerCase().includes('network') ||
      errorDetail.toLowerCase().includes('internet')
    ) {
      userFriendlyMessage = 'No se pudo conectar con el servidor';
      suggestions = 'Verificá tu conexión a internet y que el servidor esté disponible.';
    } else if (errorDetail.toLowerCase().includes('timeout')) {
      userFriendlyMessage = 'La consulta tardó demasiado tiempo';
      suggestions = 'Intentá con una pregunta más simple o volvé a intentar más tarde.';
    } else if (errorDetail.toLowerCase().includes('401') || errorDetail.toLowerCase().includes('unauthorized')) {
      userFriendlyMessage = 'Error de autenticación';
      suggestions = 'Tu sesión puede haber expirado. Intentá recargar la página.';
    } else if (errorDetail.toLowerCase().includes('500') || errorDetail.toLowerCase().includes('internal server')) {
      userFriendlyMessage = 'Error interno del servidor';
      suggestions = 'Estamos trabajando en solucionarlo. Intentá de nuevo más tarde.';
    } else if (errorDetail) {
      userFriendlyMessage = 'Error al procesar la consulta';
      suggestions = `Detalle técnico: ${errorDetail.substring(0, 150)}${errorDetail.length > 150 ? '...' : ''}`;
    }

    this.errorMessage = userFriendlyMessage;

    const errorMessage: ChatMessage = {
      from: 'ai',
      text: `**${userFriendlyMessage}**\n\n${suggestions}\n\n*Si el problema persiste, contactá al soporte técnico.*`,
      error: 'STREAM_ERROR',
      errorDetails: errorDetails,
    };

    // Reemplazar el mensaje de streaming con el error
    const streamingMessageIndex = this.messages.findIndex(m => m.isStreaming);
    if (streamingMessageIndex >= 0) {
      this.messages[streamingMessageIndex] = errorMessage;
    } else {
      this.messages.push(errorMessage);
    }

    this.scrollToBottomWhitDelay();
  }

  openErrorDetailsModal(errorDetails: any): void {
    this.dialog.open(AiDashboardErrorDetailsModalComponent, {
      width: '600px',
      data: { errorDetails },
      panelClass: 'ai-dashboard-error-details-modal',
    });
  }

  stopStream(): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
      this.sseSubscription = undefined;
    }

    // Cancelar el stream fetch actual
    this.sseService.cancelCurrentStream();
    this.sseService.closeConnection();

    // Marcar como no cargando
    this.isLoading = false;
    this.loading = false;

    if (_isDev()) {
      _log('[StreamingChatContent] Stream stopped by user');
    }
  }

  /**
   * Carga las sugerencias de insights después de completar el análisis
   */
  private loadInsightSuggestions(): void {
    _log('[StreamingChatContent] Loading insight suggestions after stream completion');

    if (this.suggestionsContainer) {
      // Pequeño delay para asegurar que el DOM esté actualizado
      setTimeout(() => {
        if (this.suggestionsContainer && this.suggestionsContainer.refresh) {
          _log('[StreamingChatContent] Calling refresh on suggestions container');
          this.suggestionsContainer.refresh();

          // Verificar si hay sugerencias después de cargarlas
          setTimeout(() => {
            this.checkForSuggestions();
          }, 100);

          // Verificación adicional con más delay
          setTimeout(() => {
            this.checkForSuggestions();
          }, 1000);
        } else {
          _log('[StreamingChatContent] Suggestions container or refresh method not available');
        }
      }, 500);
    } else {
      _log('[StreamingChatContent] No suggestions container found');
    }
  }
  /**
   * Limpia las sugerencias cuando se reinicia la conversación
   */
  private clearSuggestions(): void {
    if (this.suggestionsContainer) {
      _log('[StreamingChatContent] Clearing suggestions on conversation reset');

      // Limpiar sugerencias estableciendo una lista vacía
      if (this.suggestionsContainer.suggestions) {
        this.suggestionsContainer.suggestions = [];
      }
    }

    // Actualizar el estado de hasSuggestions
    this.hasSuggestions = false;
    this.cdr.detectChanges();
  }

  /**
   * Verifica si hay sugerencias disponibles y actualiza el estado
   */
  private checkForSuggestions(): void {
    let hasAvailableSuggestions = false;

    // Verificar si hay sugerencias en el contenedor
    if (this.suggestionsContainer) {
      // Múltiples formas de verificar si hay sugerencias
      const hasSuggestionsArray =
        this.suggestionsContainer.suggestions &&
        Array.isArray(this.suggestionsContainer.suggestions) &&
        this.suggestionsContainer.suggestions.length > 0;

      // También verificar si el componente está mostrando sugerencias visualmente
      const hasVisibleSuggestions = this.suggestionsContainer.shouldShow;

      hasAvailableSuggestions = hasSuggestionsArray || hasVisibleSuggestions;
    }

    if (this.hasSuggestions !== hasAvailableSuggestions) {
      this.hasSuggestions = hasAvailableSuggestions;
      _log('[StreamingChatContent] Suggestions state updated:', this.hasSuggestions);
      this.cdr.detectChanges();
    }
  }

  // Métodos para el template
  shouldShowRealtimeReasoning(): boolean {
    const hasCurrentContent = this.currentReasoningText.length > 0;
    const hasFinalOutput = this.finalOutput && this.finalOutput.length > 0;
    return hasCurrentContent && !hasFinalOutput;
  }

  shouldShowFinalOutput(): boolean {
    return this.hasValidOutput && this.finalOutput.length > 0;
  }

  toggleCycleHistory(): void {
    this.showCycleHistory = !this.showCycleHistory;
  }

  trackByCycle(index: number, cycle: { cycle: number; title: string; content: string }): number {
    return cycle.cycle;
  }

  onMessageAction(action: any): void {
    if ((action.action === 'view_suggestion_detail' || action.action === 'view_insights') && action.payload) {
      this.onOpenSuggestionDetail(action.payload);
    }
  }

  copyMessageToClipboard(message: ChatMessage, scope: 'message' | 'conversation' = 'message'): void {
    if (!message || !message.text) return;

    const text = scope === 'conversation' ? this._buildConversationText() : message.text;

    navigator.clipboard.writeText(text).then(
      () => {
        const key = scope === 'conversation' ? 'AI_CHAT.MESSAGES.COPY_SUCCESS_CONVERSATION' : 'AI_CHAT.MESSAGES.COPY_SUCCESS';
        this.snackBar.open(this.translate.instant(key), '', {
          duration: 2000,
          panelClass: ['success-snackbar'],
        });
      },
      err => {
        _error('Error copying to clipboard:', err);
        this.snackBar.open(this.translate.instant('AI_CHAT.MESSAGES.COPY_ERROR'), '', {
          duration: 2000,
          panelClass: ['error-snackbar'],
        });
      }
    );
  }

  private _buildConversationText(): string {
    const userLabel = this.userDataName || this.translate.instant('AI_CHAT.MESSAGES.USER_DEFAULT');
    const aiLabel = this.translate.instant('AI_CHAT.MESSAGES.AI_NAME_AGENT');
    return this.messages
      .filter(m => !m.isStreaming && m.text && !m.actionButton)
      .map(m => `${m.from === 'user' ? userLabel : aiLabel}:\n${m.text}`)
      .join('\n\n---\n\n');
  }

  private _buildConversationHtml(): string {
    const userLabel = this.userDataName || this.translate.instant('AI_CHAT.MESSAGES.USER_DEFAULT');
    const aiLabel = this.translate.instant('AI_CHAT.MESSAGES.AI_NAME_AGENT');
    return this.messages
      .filter(m => !m.isStreaming && m.text && !m.actionButton)
      .map(m => {
        const label = m.from === 'user' ? userLabel : aiLabel;
        const bubbleStyle = m.from === 'user' ? 'margin:16px 0;padding:10px 14px;background:#eef2ff;border-radius:8px;' : 'margin:16px 0;';
        const body = m.from === 'user' ? `<p style="margin:0;white-space:pre-wrap;">${m.text}</p>` : markdownToHtmlString(m.text);
        return `<div style="${bubbleStyle}"><strong style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:.5px;">${label}</strong>${body}</div>`;
      })
      .join('<hr style="border:none;border-top:1px solid #e0e0e0;margin:8px 0;">');
  }

  downloadAsPdf(message: ChatMessage, scope: 'message' | 'conversation' = 'message'): void {
    if (!message || !message.text) return;

    const htmlContent = scope === 'conversation' ? this._buildConversationHtml() : markdownToHtmlString(message.text);
    const title = scope === 'conversation' ? this.translate.instant('AI_CHAT.MESSAGES.PDF_CONVERSATION_TITLE') : 'Prisma Agent';

    // SVG logo with palette inverted: original uses fill:#fff (for dark bg) → changed to #1a2b4a (dark navy)
    const logoSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1131.28 335.19" style="height:38px;display:block;">
      <style>.cls-1{fill:#1a2b4a;}</style>
      <path class="cls-1" d="m50,103.19v134.16h134.16V103.19H50Zm115.06,67.08l-23.99,41.55h-47.98l-23.99-41.55,23.99-41.55h47.98l23.99,41.55Z"/>
      <path class="cls-1" d="m1054.02,224.13c0-7.69,5.92-13.62,13.58-13.62s13.68,5.92,13.68,13.62-5.92,13.65-13.68,13.65-13.58-5.92-13.58-13.65Zm25.27,0c0-6.64-5.07-11.72-11.68-11.72s-11.68,5.11-11.68,11.72,5.07,11.65,11.68,11.65,11.68-5.07,11.68-11.65Zm-16.92,7.1v-14.14c0-.2.13-.33.33-.33h6.48c2.49,0,4.35,1.9,4.35,4.45,0,1.64-.79,2.91-2.06,3.67-.16.13-.2.26-.13.46l2.45,5.83c.1.23-.03.39-.26.39h-2.52c-.16,0-.29-.07-.36-.26l-2.19-5.56c-.07-.2-.2-.26-.36-.26h-2.45c-.2,0-.33.13-.33.33v5.43c0,.2-.13.33-.33.33h-2.29c-.2,0-.33-.13-.33-.33Zm8.22-10.02c0-1.15-.75-1.87-2.06-1.87h-2.88c-.2,0-.33.13-.33.33v2.98c0,.2.13.33.33.33h2.88c1.34,0,2.06-.75,2.06-1.77Z"/>
      <path class="cls-1" d="m236.12,283.88V104.09c0-.79.53-1.32,1.32-1.32h28.32c.66,0,1.19.26,1.45,1.05l3.29,8.82c.4,1.05,1.19,1.05,1.84.4,8.69-8.56,22.26-13.04,37.41-13.04,36.75,0,64.27,25.68,64.27,70.07s-27.53,70.33-65.85,70.33c-14.09,0-26.87-3.82-36.09-11.06-.92-.66-1.58-.26-1.58.79v53.74c0,.79-.53,1.32-1.32,1.32h-31.74c-.79,0-1.32-.53-1.32-1.32Zm103.52-113.8c0-26.08-14.75-41.36-35.3-41.36s-35.43,15.28-35.43,41.36,14.88,41.75,35.43,41.75,35.3-15.81,35.3-41.75Z"/>
      <path class="cls-1" d="m389.7,236.46V104.09c0-.79.53-1.32,1.32-1.32h28.71c.66,0,1.32.26,1.58.92l5.66,11.85c.4.92,1.19.92,1.71,0,5.27-8.82,14.22-12.78,25.42-12.78h15.01c.79,0,1.32.53,1.32,1.32v28.05c0,.79-.53,1.32-1.32,1.32h-19.49c-15.54,0-25.29,9.75-25.29,32.53v70.46c0,.79-.53,1.32-1.32,1.32h-32.01c-.79,0-1.32-.53-1.32-1.32Z"/>
      <path class="cls-1" d="m486.37,236.46V104.09c0-.73.59-1.32,1.32-1.32h32.01c.73,0,1.32.59,1.32,1.32v132.37c0,.73-.59,1.32-1.32,1.32h-32.01c-.73,0-1.32-.59-1.32-1.32Z"/>
      <path class="cls-1" d="m538,196.42c-.13-.92.4-1.45,1.19-1.45h30.69c.79,0,1.32.4,1.45,1.19,3.95,13.83,15.94,18.57,30.69,18.57,19.36,0,27.53-7.51,27.53-16.86,0-13.57-16.86-13.7-36.09-14.62-24.89-1.05-54-7.11-54-39.51,0-24.89,19.23-43.73,59.01-43.73,32.14,0,57.69,12.51,61.11,42.81.13.92-.53,1.45-1.32,1.45h-30.42c-.79,0-1.32-.4-1.45-1.19-3.29-12.91-13.43-17.12-27.92-17.12-16.46,0-26.87,5.53-26.87,15.81,0,11.59,14.36,12.91,32.14,13.57,25.16.79,57.95,3.56,57.95,40.3,0,28.45-19.89,44.78-60.19,44.78-34.38,0-58.48-14.09-63.48-43.99Z"/>
      <path class="cls-1" d="m678.4,236.46V104.09c0-.79.53-1.32,1.32-1.32h28.58c.66,0,1.19.26,1.45,1.05l2.9,6.98c.4.79,1.05,1.05,1.84.53,8.56-6.98,20.55-11.33,35.96-11.33s28.71,4.48,36.88,13.04c.66.53,1.19.66,1.84,0,10.27-8.96,23.97-13.04,42.02-13.04,34.64,0,51.76,15.81,51.76,47.68v88.77c0,.79-.53,1.32-1.32,1.32h-31.74c-.79,0-1.32-.53-1.32-1.32v-81.66c0-16.6-9.22-26.08-25.16-26.08s-25.55,10.01-25.55,27v80.74c0,.79-.53,1.32-1.32,1.32h-31.74c-.79,0-1.32-.53-1.32-1.32v-81.66c0-16.6-9.35-26.08-25.29-26.08s-25.16,9.22-25.16,27v80.74c0,.79-.53,1.32-1.32,1.32h-32.01c-.79,0-1.32-.53-1.32-1.32Z"/>
      <path class="cls-1" d="m900.59,196.95c0-25.29,18.04-42.81,52.42-42.81h40.7c.79,0,1.32-.53,1.32-1.32v-2.11c0-15.28-9.48-23.71-27.66-23.71-15.54,0-24.37,6.32-27,17.39-.26.79-.66,1.19-1.45,1.19h-32.14c-.79,0-1.45-.53-1.19-1.45,4.08-27.53,25.42-44.12,62.3-44.12,38.99,0,61.51,20.02,61.51,51.37v53.74c0,3.69,1.71,5.4,5.53,5.4h5.4c.79,0,1.32.53,1.32,1.32v24.63c0,.79-.53,1.32-1.32,1.32h-18.31c-10.01,0-17.12-3.03-20.55-9.61-.4-.92-1.05-1.05-1.71-.53-10.27,9.75-26.87,12.78-42.28,12.78-36.09,0-56.9-16.46-56.9-43.46Zm94.96-13.43v-2.63c0-.79-.53-1.32-1.32-1.32h-36.75c-15.67,0-22.39,5.66-22.39,16.07,0,11.59,8.82,18.57,26.74,18.57,20.02,0,33.72-9.09,33.72-30.69Z"/>
      <rect class="cls-1" x="486.47" y="50" width="34.37" height="32.15" rx="1.31" ry="1.31"/>
    </svg>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 13px; color: #222; margin: 32px 48px; line-height: 1.6; }
            .pdf-header { display: flex; align-items: center; padding-bottom: 14px; margin-bottom: 24px; border-bottom: 2px solid #1a2b4a; }
            .pdf-header svg { height: 38px; }
            h1 { font-size: 20px; border-bottom: 1px solid #ddd; padding-bottom: 6px; }
            h2 { font-size: 16px; margin-top: 20px; }
            h3 { font-size: 14px; margin-top: 16px; }
            table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; }
            th { background: #f0f0f0; font-weight: bold; }
            .csv-table { border-collapse: collapse; width: auto; margin: 12px 0 20px; font-size: 12px; }
            .csv-table th, .csv-table td { border: 1px solid #aac; padding: 5px 12px; text-align: left; font-family: 'Courier New', monospace; white-space: nowrap; }
            .csv-table th { background: #dbe7fc; font-weight: bold; }
            .csv-table tr:nth-child(even) td { background: #f4f6fb; }
            pre { background: #f5f5f5; padding: 10px; border-radius: 4px; font-size: 11px; overflow-x: auto; white-space: pre-wrap; word-break: break-all; }
            code { background: #f0f0f0; padding: 1px 4px; border-radius: 3px; font-size: 11px; }
            ul, ol { padding-left: 20px; }
            hr { border: none; border-top: 1px solid #ddd; margin: 16px 0; }
            @media print { body { margin: 20px; } }
          </style>
        </head>
        <body>
          <div class="pdf-header">${logoSvg}</div>
          ${htmlContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }

  isWelcomeOrSystemMessage(message: ChatMessage): boolean {
    if (!message) return true;
    // Excluir el primer mensaje del agente (bienvenida), que siempre está en la posición 0
    if (this.messages[0] === message) return true;
    // Excluir mensajes que son solo botones de acción (no tienen contenido propio que copiar)
    if (message.actionButton) return true;
    // Excluir mensajes de error
    if (message.error) return true;
    return false;
  }

  // Métodos requeridos por ChatContentComponent interface
  sendMessage(message: string): void {
    this.onSendMessage(message);
  }

  onChatTypeChange(newType: ChatType): void {
    this.chatTypeChange.emit(newType);
  }

  // Métodos de utilidad heredados
  async scrollToBottomWhitDelay($time = 100) {
    await _timeout($time);
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error(err);
    }
  }

  toggleHistory(): void {
    this.isHistoryOpen = !this.isHistoryOpen;
    setTimeout(() => {
      // Recalcular layout si es necesario o trigger resize
    }, 200);
  }

  startDragging(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.clientX;
    this.startWidth = this.sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const deltaX = event.clientX - this.startX;
    let newWidth = this.startWidth + deltaX;

    if (newWidth < this.minSidebarWidth) newWidth = this.minSidebarWidth;
    if (newWidth > this.maxSidebarWidth) newWidth = this.maxSidebarWidth;

    this.sidebarWidth = newWidth;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (this.isDragging) {
      this.isDragging = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }
}
